import { computeOrderItemTotals, hasPricedLines } from '@/utils/orderTotals';

type CompanyPricelistCurrency = {
  code?: string;
  name?: string;
  symbol?: string;
};

export type OrderItemCurrencySource = {
  currency?: {
    net_price?: { code?: string; name?: string; symbol?: string };
  } | null;
  billing_company_id?: number | null;
  billing_company?: {
    pricelist?: { currency?: CompanyPricelistCurrency };
  } | null;
  billingCompany?: {
    pricelist?: { currency?: CompanyPricelistCurrency };
  } | null;
  price_list_detail?: { currency_code?: string | null } | null;
  merchant_currency?: CompanyPricelistCurrency | null;
  order?: {
    company?: {
      pricelist?: { currency?: CompanyPricelistCurrency };
    };
    additional_fields?: {
      currency?: { net_price?: { code?: string } };
    };
  };
};

function pickCurrencyCode(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  return s;
}

function orderItemCurrencyIsEmpty(item: OrderItemCurrencySource | null | undefined): boolean {
  if (item?.currency == null) return true;
  if (Array.isArray(item.currency) && item.currency.length === 0) return true;
  return pickCurrencyCode(item.currency?.net_price?.code) === '';
}

function billingCompanyPricelistCurrencyCode(item: OrderItemCurrencySource): string {
  if (!item.billing_company_id) return '';
  const fromPricelist = pickCurrencyCode(
    item.billing_company?.pricelist?.currency?.code ??
      item.billingCompany?.pricelist?.currency?.code,
  );
  if (fromPricelist) return fromPricelist;
  // Dashboard list: `merchant_currency` is billing company when `billing_company_id` is set.
  return pickCurrencyCode(item.merchant_currency?.code);
}

function orderCompanyPricelistCurrencyCode(item: OrderItemCurrencySource): string {
  const fromPricelist = pickCurrencyCode(item.order?.company?.pricelist?.currency?.code);
  if (fromPricelist) return fromPricelist;
  if (!item.billing_company_id) {
    return pickCurrencyCode(item.merchant_currency?.code);
  }
  return '';
}

/** Merge order-item row with order detail for currency resolution. */
export function buildOrderItemCurrencySource(
  item?: Record<string, unknown> | null,
  order?: Record<string, unknown> | null,
): OrderItemCurrencySource {
  const it = (item ?? {}) as Record<string, unknown>;
  const ord = (order ?? {}) as Record<string, unknown>;
  return {
    currency: it.currency as OrderItemCurrencySource['currency'],
    billing_company_id: it.billing_company_id as number | null | undefined,
    billing_company: (it.billing_company ?? it.billingCompany) as OrderItemCurrencySource['billing_company'],
    billingCompany: (it.billingCompany ?? it.billing_company) as OrderItemCurrencySource['billingCompany'],
    merchant_currency: it.merchant_currency as OrderItemCurrencySource['merchant_currency'],
    price_list_detail: it.price_list_detail as OrderItemCurrencySource['price_list_detail'],
    order: {
      company: ord.company,
      additional_fields: ord.additional_fields,
    } as OrderItemCurrencySource['order'],
  };
}

/**
 * Net price currency for UI (matches Stripe invoice currency resolution):
 * 1. `order_items.currency.net_price` when set
 * 2. Billing company pricelist when `billing_company_id` is set
 * 3. Order company pricelist
 */
export function getOrderItemNetPriceCurrencyCode(
  item: OrderItemCurrencySource | null | undefined,
): string {
  if (!item) return '';

  if (!orderItemCurrencyIsEmpty(item)) {
    return pickCurrencyCode(item.currency?.net_price?.code);
  }

  const billingCode = billingCompanyPricelistCurrencyCode(item);
  if (billingCode) return billingCode;

  const orderCode = orderCompanyPricelistCurrencyCode(item);
  if (orderCode) return orderCode;

  return (
    pickCurrencyCode(item.price_list_detail?.currency_code) ||
    pickCurrencyCode(item.order?.additional_fields?.currency?.net_price?.code) ||
    ''
  );
}

/** Full currency bucket for labels (code / symbol / name). */
export function getOrderItemNetPriceCurrency(
  item?: Record<string, unknown> | null,
  order?: Record<string, unknown> | null,
): { code?: string; symbol?: string; name?: string } {
  const src = buildOrderItemCurrencySource(item, order);
  const code = getOrderItemNetPriceCurrencyCode(src);
  if (!code) return {};

  if (!orderItemCurrencyIsEmpty(src)) {
    return {
      code,
      symbol: src.currency?.net_price?.symbol,
      name: src.currency?.net_price?.name,
    };
  }

  const company = src.billing_company_id
    ? (src.billing_company ?? src.billingCompany)
    : src.order?.company;
  const pricelistCurrency = company?.pricelist?.currency;
  return {
    code,
    symbol: pricelistCurrency?.symbol ?? src.merchant_currency?.symbol,
    name: pricelistCurrency?.name ?? src.merchant_currency?.name,
  };
}

/**
 * Amount to show next to "Order Price" when API leaves `order_item_final_price` at 0
 * but `total_price` (products + order lines) is populated.
 *
 * Priority (first non-zero finite wins): `order_item_final_price` → `total_price` →
 * `total_final_price` → `order_final_price`. If all zero, returns the first defined finite value.
 */
export function getOrderItemPriceInfoDisplayAmount(
  priceInfo: Record<string, unknown> | null | undefined,
): number {
  if (priceInfo == null || typeof priceInfo !== 'object') {
    return 0;
  }

  const num = (v: unknown): number => {
    const x = Number(v);
    return Number.isFinite(x) ? x : NaN;
  };

  const orderItemFinal = num(priceInfo.order_item_final_price);
  const totalPrice = num(priceInfo.total_price);
  const totalFinal = num(priceInfo.total_final_price);
  const orderFinal = num(priceInfo.order_final_price);

  const nonzero = (n: number) => Number.isFinite(n) && n !== 0;

  if (nonzero(orderItemFinal)) return orderItemFinal;
  if (nonzero(totalPrice)) return totalPrice;
  if (nonzero(totalFinal)) return totalFinal;
  if (nonzero(orderFinal)) return orderFinal;

  if (Number.isFinite(orderItemFinal)) return orderItemFinal;
  if (Number.isFinite(totalPrice)) return totalPrice;
  if (Number.isFinite(totalFinal)) return totalFinal;
  if (Number.isFinite(orderFinal)) return orderFinal;
  return 0;
}

/**
 * Amount for "Order Price" on an order item: derived from its lines (unit price × qty, manual
 * lines, discount, shipping) whenever the item carries them, so a stale saved
 * `order_item_final_price` (e.g. saved before a second product was added) cannot show.
 * Falls back to the saved `price_info` when the item has no line data (e.g. a slim list row).
 */
export function getOrderItemDisplayTotal(orderItem: Record<string, any> | null | undefined): number {
  if (hasPricedLines(orderItem)) {
    return computeOrderItemTotals(orderItem).finalTotal;
  }
  return getOrderItemPriceInfoDisplayAmount(orderItem?.price_info);
}

